import {Component, inject, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {ICharityPayout} from "../../../interfaces/charity/ICharityPayout";
import {DialogBaseComponent} from "../dialog-base/dialog-base.component";
import {DatePipe} from "@angular/common";
import {PipesModule} from "../../../pipes/pipes.module";
import {CharityPayoutService} from "../../../services/charity-payout.service";
import {ICharityPayoutNote} from "../../../interfaces/charity/ICharityPayoutNote";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {MatFormField, MatLabel, MatPrefix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatButton, MatIconButton} from "@angular/material/button";
import {FormsModule} from "@angular/forms";
import {SnackbarService} from "../../../services/snackbar.service";
import {PermissionTypes} from "../../../enum/PermissionTypes";
import {PermissionsService} from "../../../services/permissions.service";
import {MatCard, MatCardContent} from "@angular/material/card";
import {MatDivider} from "@angular/material/divider";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {IUpdateCharityPayoutCommand} from "../../../interfaces/commands/IUpdateCharityPayoutCommand";
import {ICreateCharityPayoutNoteCommand} from "../../../interfaces/commands/ICreateCharityPayoutNoteCommand";
import {catchError, finalize, switchMap, tap} from "rxjs/operators";
import {EMPTY} from "rxjs";

@Component({
  selector: 'app-charity-payout-details',
  templateUrl: './charity-payout-details.component.html',
  standalone: true,
  imports: [
    DialogBaseComponent,
    DatePipe,
    PipesModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatButton,
    FormsModule,
    MatCard,
    MatCardContent,
    MatDivider,
    MatIconButton,
    MatIcon,
    MatTooltip,
    MatPrefix
  ],
  styleUrls: ['./charity-payout-details.component.scss']
})
export class CharityPayoutDetailsComponent implements OnInit {

  private charityPayoutService: CharityPayoutService = inject(CharityPayoutService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private permissionsService: PermissionsService = inject(PermissionsService);

  public notes: ICharityPayoutNote[] = [];
  public newNoteText: string = '';
  public isSubmittingNote: boolean = false;
  public isLoadingNotes: boolean = false;
  public canAddNotes: boolean = false;
  public isEditingAmount: boolean = false;
  public editedAmount: number | null = null;
  public editNoteText: string = '';

  constructor(@Inject(MAT_DIALOG_DATA) public data: {
    payout: ICharityPayout,
    charityName: string,
    gameId?: string
  }) {
  }

  ngOnInit(): void {
    this.canAddNotes = this.permissionsService.userHasPermission(PermissionTypes.ModifyCharityPayouts);
    this.fetchNotes();
  }

  fetchNotes(): void {
    this.isLoadingNotes = true;
    this.charityPayoutService.fetchNotesByCharityPayoutId(this.data.payout.Id, this.data.gameId).subscribe({
      next: (notes) => {
        this.notes = notes;
        this.isLoadingNotes = false;
      },
      error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
        this.isLoadingNotes = false;
      }
    });
  }

  addNote(): void {
    if (!this.newNoteText.trim() || this.isSubmittingNote) {
      return;
    }

    if (!this.data.gameId) {
      this.snackbarService.openErrorSnackBar('GameId is required to add a note');
      return;
    }

    const command: ICreateCharityPayoutNoteCommand = {
      CharityPayoutId: this.data.payout.Id,
      Note: this.newNoteText,
      GameId: this.data.gameId
    };

    this.isSubmittingNote = true;
    this.charityPayoutService.createCharityPayoutNote(command).pipe(
      tap(() => {
        this.snackbarService.openSuccessfulSnackBar('Note added successfully!');
        this.newNoteText = '';
      }),
      switchMap(() => this.charityPayoutService.fetchNotesByCharityPayoutId(this.data.payout.Id, this.data.gameId)),
      tap((notes) => {
        this.notes = notes;
      }),
      catchError((err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
        return EMPTY;
      }),
      finalize(() => {
        this.isSubmittingNote = false;
      })
    ).subscribe();
  }

  startEditingAmount(): void {
    this.isEditingAmount = true;
    // Convert cents to dollars for editing
    this.editedAmount = this.data.payout.PayoutAmount / 100;
    this.editNoteText = '';
  }

  cancelEditingAmount(): void {
    this.isEditingAmount = false;
    this.editedAmount = null;
    this.editNoteText = '';
  }

  saveEditedAmount(): void {
    if (this.editedAmount === null || this.editedAmount <= 0) {
      return;
    }

    // Convert to number in case it's a string from the input
    const editedAmountNumber = typeof this.editedAmount === 'string' ? parseFloat(this.editedAmount) : this.editedAmount;

    // Check if amount has actually changed
    const newAmountInCents = Math.round(editedAmountNumber * 100);
    if (newAmountInCents === this.data.payout.PayoutAmount) {
      this.snackbarService.openErrorSnackBar('Payout amount has not changed');
      return;
    }

    // Require a note when editing
    if (!this.editNoteText.trim()) {
      this.snackbarService.openErrorSnackBar('A note explaining the change is required');
      return;
    }

    // Validate the amount is a valid number
    if (isNaN(editedAmountNumber) || !isFinite(editedAmountNumber)) {
      this.snackbarService.openErrorSnackBar('Please enter a valid amount');
      return;
    }

    // Validate gameId is provided
    if (!this.data.gameId) {
      this.snackbarService.openErrorSnackBar('GameId is required to update payout');
      return;
    }

    const updateCommand: IUpdateCharityPayoutCommand = {
      CharityPayoutId: this.data.payout.Id,
      PayoutAmount: newAmountInCents,
      Note: this.editNoteText,
      GameId: this.data.gameId
    };

    this.isSubmittingNote = true;
    this.charityPayoutService.updateCharityPayout(updateCommand).pipe(
      tap(() => {
        this.snackbarService.openSuccessfulSnackBar('Payout amount updated successfully!');
        // Update the local data
        this.data.payout.PayoutAmount = newAmountInCents;
        this.isEditingAmount = false;
        this.editedAmount = null;
        this.editNoteText = '';
      }),
      switchMap(() => this.charityPayoutService.fetchNotesByCharityPayoutId(this.data.payout.Id, this.data.gameId)),
      tap((notes) => {
        this.notes = notes;
      }),
      catchError((err) => {
        this.errorHandlingService.displayDialogLevelErrorMessage(err);
        return EMPTY;
      }),
      finalize(() => {
        this.isSubmittingNote = false;
      })
    ).subscribe();
  }
}
